/**
 * MonEspaceAgentPage — la brique « Mon dossier personnel » (mig 253).
 *
 * ─── À QUI ELLE S'ADRESSE ───────────────────────────────────────────────────
 *
 * À TOUS les agents, quel que soit leur service. C'est la première brique de
 * VPC qui ne se cloisonne pas : un agent des espaces verts et un agent
 * d'accueil y voient exactement la même chose.
 *
 * ─── L'ORDRE DES SECTIONS N'EST PAS COSMÉTIQUE ──────────────────────────────
 *
 * Les numéros d'urgence sont AVANT les liens utiles. Quand on ouvre cet écran
 * en situation de crise, on ne fait pas défiler. Le PCS (art. L731-3 CSI) sert
 * précisément à ce qu'un agent trouve le bon numéro sans réfléchir.
 *
 * ─── CE QU'ON N'A PAS DÉMÉNAGÉ ──────────────────────────────────────────────
 *
 * Le dossier agent, les congés, les frais et le planning restent dans la brique
 * RH (arbitrage (b) du 26/07 : le neuf seulement). Les raccourcis vers eux
 * arrivent en vague A2.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Alert, Badge, Button, ModuleHeader, SectionEyebrow } from '@/components/ui';
import { EmptyState } from '@/components/ui/EmptyState';
import { PhoneLink } from '@/components/ui/PhoneLink';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import type {
  AgentsLienCategorie, AgentsLienUtile, AgentsContactUrgence, DiffusionAAcquitter,
} from '@roronoa-collectivites/shared-types';
import {
  UserRound, Phone, LinkIcon, Siren, ExternalLink, Settings2, Loader2, Info,
  FolderOpen, CalendarDays, Receipt, FileText, BellRing, CheckCheck, Paperclip,
  Users,
} from 'lucide-react';
import { EcheancesPersoCard } from '@/components/echeances/EcheancesPersoCard';

/**
 * Les quatre renvois vers la brique RH. Les cibles correspondent une à une aux
 * onglets ouverts aux agents (`AGENT_TABS` dans `RhNav.tsx`) et à la liste
 * blanche `ONGLETS_URL` de `RhPaiePage` : un agent qui bricolerait l'URL
 * n'ouvrirait aucun écran de gestion.
 */
const RACCOURCIS = [
  // Item 6.3 — le seul raccourci de cette liste qui ne mène pas à la brique RH :
  // l'annuaire n'est pas « mon » dossier, c'est celui des autres.
  { href: '/mon-espace/annuaire', label: 'Annuaire des agents',
    description: 'Joindre un collègue, voir qui est là aujourd\'hui', icon: Users },
  { href: '/rh?vue=perso&onglet=mesdemandes', label: 'Mes congés et absences',
    description: 'Demander, suivre, consulter mes soldes', icon: CalendarDays },
  { href: '/rh?vue=perso&onglet=monplanning', label: 'Mon planning',
    description: 'Mes horaires et mes affectations', icon: CalendarDays },
  { href: '/rh?vue=perso&onglet=notesfrais', label: 'Mes notes de frais',
    description: 'Déclarer un déplacement, suivre un remboursement', icon: Receipt },
  // 28/07 : ce raccourci ouvrait les DOCUMENTS de la commune, pas le dossier.
  // Les deux existent maintenant, et chacun mène où son libellé l'annonce.
  { href: '/rh?vue=perso&onglet=mondossier', label: 'Mon dossier',
    description: 'Mon état civil, ma carrière, mes coordonnées', icon: UserRound },
  { href: '/rh?vue=perso&onglet=documents', label: 'Mes documents',
    description: 'Règlement intérieur, notes de service, attestations', icon: FileText },
] as const;

/** Regroupe les liens par catégorie, en respectant l'ordre défini par la mairie. */
function grouper(
  liens: AgentsLienUtile[],
  categories: AgentsLienCategorie[]
): { id: string | null; libelle: string; liens: AgentsLienUtile[] }[] {
  const groupes: { id: string | null; libelle: string; liens: AgentsLienUtile[] }[] = [];
  for (const c of categories) {
    const dedans = liens.filter((l) => l.categorieId === c.id);
    if (dedans.length > 0) groupes.push({ id: c.id, libelle: c.libelle, liens: dedans });
  }
  const orphelins = liens.filter((l) => !l.categorieId);
  if (orphelins.length > 0) groupes.push({ id: null, libelle: 'Divers', liens: orphelins });
  return groupes;
}

export function MonEspaceAgentPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [categories, setCategories] = useState<AgentsLienCategorie[]>([]);
  const [liens, setLiens] = useState<AgentsLienUtile[]>([]);
  const [urgences, setUrgences] = useState<AgentsContactUrgence[]>([]);
  const [attentes, setAttentes] = useState<DiffusionAAcquitter[]>([]);
  const [acquittement, setAcquittement] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Le bouton « Configurer » ne s'affiche que pour qui peut réellement écrire.
  // Même règle que les permissions posées en migration 253.
  const peutConfigurer =
    user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [data, att] = await Promise.all([
        api.monEspace.tout(),
        // Un échec ici ne doit pas vider l'espace agent : les numéros d'urgence
        // passent avant tout le reste.
        api.diffusions.mesAttentes().catch(() => [] as DiffusionAAcquitter[]),
      ]);
      setCategories(data.categories);
      setLiens(data.liens);
      setUrgences(data.urgences);
      setAttentes(att);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  const groupes = useMemo(() => grouper(liens, categories), [liens, categories]);

  // L'acquittement est DÉFINITIF côté serveur : on retire la carte tout de suite
  // plutôt que de recharger, et on ne propose aucun moyen de revenir dessus.
  async function repondre(id: string) {
    setAcquittement(id); setError(null);
    try {
      await api.diffusions.acquitter(id);
      setAttentes((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Impossible d\'enregistrer votre réponse');
    } finally { setAcquittement(null); }
  }

  return (
    <div className="min-h-screen text-ink">
      {/* Le bandeau mairie manquait : sans lui, aucun retour au tableau de bord
          depuis ces écrans. Remonté par la mairie le 28/07. */}
      <AppHeader module="Mon dossier personnel" accent="rh" />
      <ModuleHeader
        accent="rh"
        icon={UserRound}
        eyebrow="Espace interne"
        title="Mon dossier personnel"
        trailing={
          peutConfigurer ? (
            <button
              type="button"
              onClick={() => navigate('/admin/mon-espace')}
              className="inline-flex h-9 items-center gap-2 rounded-r-lg border border-border bg-surface/60 px-3 text-xs font-medium text-ink transition-colors hover:border-rh/40"
            >
              <Settings2 className="h-4 w-4" aria-hidden="true" />
              Configurer
            </button>
          ) : undefined
        }
      />

      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        {/* M6 (retour Manon) — échéances personnalisées. */}
        <div className="mb-6"><EcheancesPersoCard /></div>
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        {loading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Chargement de votre espace…
          </div>
        ) : (
          <>
            {/* ── Ce qui attend une réponse de VOUS (item 5.25) ─────────────
                En tête, et ça ne descend pas : un message ordinaire glisse dans
                la boîte, une demande qui attend un retour doit rester visible
                tant qu'on n'y a pas répondu. Le bloc disparaît de lui-même une
                fois la réponse donnée. */}
            {attentes.length > 0 && (
              <section aria-labelledby="titre-attentes" className="mb-8">
                <h2
                  id="titre-attentes"
                  className="flex items-center gap-2 font-display text-lg font-semibold tracking-r-tight text-ink"
                >
                  <BellRing className="h-5 w-5 text-urgent" aria-hidden="true" />
                  On attend votre réponse
                </h2>

                <ul className="mt-3 space-y-2.5">
                  {attentes.map((d) => (
                    <li key={d.id}>
                      <Card variant="glass" padded className="border-urgent/30">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-ink">{d.objet}</div>
                            <div className="text-xs text-ink-subtle">
                              De {d.expediteur}
                              {d.dateLimite && ` · avant le ${new Intl.DateTimeFormat('fr-FR', {
                                day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris',
                              }).format(new Date(`${d.dateLimite}T12:00:00Z`))}`}
                            </div>
                          </div>
                          <Badge variant="urgent" size="sm">
                            {d.retourAttendu === 'action_faite' ? 'À faire' : 'À lire'}
                          </Badge>
                        </div>

                        <p className="mt-2 whitespace-pre-line text-sm text-ink-muted">{d.corps}</p>

                        {d.documents.length > 0 && (
                          <ul className="mt-3 space-y-1">
                            {d.documents.map((doc) => (
                              <li key={doc.id} className="flex items-center gap-2 text-sm">
                                <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                                <button
                                  type="button"
                                  onClick={() => navigate('/rh?vue=perso&onglet=documents')}
                                  className="text-rh underline underline-offset-2"
                                >
                                  {doc.titre}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-3">
                          <Button
                            variant="rh" size="sm"
                            loading={acquittement === d.id}
                            onClick={() => void repondre(d.id)}
                          >
                            <CheckCheck className="h-4 w-4" aria-hidden="true" />
                            {d.retourAttendu === 'action_faite' ? 'C\'est fait' : 'J\'ai pris connaissance'}
                          </Button>
                        </div>
                      </Card>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ── Numéros d'urgence (item 8.11) ────────────────────────────── */}
            <section aria-labelledby="titre-urgences">
              <SectionEyebrow as="div">Plan communal de sauvegarde</SectionEyebrow>
              <h2
                id="titre-urgences"
                className="mt-1 flex items-center gap-2 font-display text-lg font-semibold tracking-r-tight text-ink"
              >
                <Siren className="h-5 w-5 text-urgent" aria-hidden="true" />
                Numéros d'urgence
              </h2>

              {urgences.length === 0 ? (
                <Card variant="glass" padded className="mt-3">
                  <EmptyState
                    icon={Phone}
                    tone="neutral"
                    title="Aucun numéro enregistré"
                    description={
                      peutConfigurer
                        ? 'La mairie n\'a pas encore saisi son annuaire d\'urgence. Vous pouvez le renseigner depuis « Configurer ».'
                        : 'La mairie n\'a pas encore saisi son annuaire d\'urgence.'
                    }
                  />
                </Card>
              ) : (
                <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
                  {urgences.map((c) => (
                    <li key={c.id}>
                      <Card variant="glass" padded className="h-full">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium text-ink">{c.libelle}</div>
                            {c.organisme && (
                              <div className="text-xs text-ink-subtle truncate">{c.organisme}</div>
                            )}
                          </div>
                          {c.rolePcs && <Badge variant="urgent" size="sm">{c.rolePcs}</Badge>}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <PhoneLink
                            number={c.telephone}
                            className="inline-flex h-9 items-center gap-2 rounded-r-lg border border-border bg-surface/60 px-3 text-sm font-medium text-ink transition-colors hover:border-urgent/40"
                          />
                          {c.telephoneSecondaire && (
                            <PhoneLink
                              number={c.telephoneSecondaire}
                              className="inline-flex h-9 items-center gap-2 rounded-r-lg border border-border bg-surface/40 px-3 text-xs text-ink-muted transition-colors hover:border-urgent/40"
                            />
                          )}
                        </div>

                        {c.note && <p className="mt-2 text-xs text-ink-muted">{c.note}</p>}
                      </Card>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ── Liens utiles (item 6.4) ─────────────────────────────────── */}
            <section aria-labelledby="titre-liens" className="mt-8">
              <SectionEyebrow as="div">Bibliothèque de la commune</SectionEyebrow>
              <h2
                id="titre-liens"
                className="mt-1 flex items-center gap-2 font-display text-lg font-semibold tracking-r-tight text-ink"
              >
                <LinkIcon className="h-5 w-5 text-rh" aria-hidden="true" />
                Liens utiles
              </h2>

              {groupes.length === 0 ? (
                <Card variant="glass" padded className="mt-3">
                  <EmptyState
                    icon={LinkIcon}
                    tone="neutral"
                    title="Aucun lien pour le moment"
                    description={
                      peutConfigurer
                        ? 'Ajoutez les outils, notices et sites que vos agents utilisent tous les jours depuis « Configurer ».'
                        : 'La mairie n\'a pas encore ajouté de lien à cet espace.'
                    }
                  />
                </Card>
              ) : (
                <div className="mt-3 space-y-5">
                  {groupes.map((g) => (
                    <div key={g.id ?? 'divers'}>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                        {g.libelle}
                      </h3>
                      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                        {g.liens.map((l) => (
                          <li key={l.id}>
                            <a
                              href={l.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex h-full items-start gap-3 rounded-r-lg border border-border bg-surface/55 p-3 backdrop-blur-glass transition-all hover:border-rh/40 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rh focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                            >
                              <ExternalLink
                                className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-subtle"
                                aria-hidden="true"
                              />
                              <span className="min-w-0">
                                <span className="block font-medium text-ink">{l.libelle}</span>
                                {l.description && (
                                  <span className="mt-0.5 block text-xs text-ink-muted">
                                    {l.description}
                                  </span>
                                )}
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── Mes raccourcis (vague A2) ────────────────────────────────
                Rien n'a déménagé : ces quatre écrans VIVENT dans la brique RH
                (arbitrage (b) du 26/07). Ce sont des renvois, pas des copies —
                une copie aurait fatalement divergé. Les cibles sont exactement
                les quatre onglets qu'un agent a le droit de voir
                (`AGENT_TABS` dans RhNav). */}
            <section aria-labelledby="titre-raccourcis" className="mt-8">
              <SectionEyebrow as="div">Ressources humaines</SectionEyebrow>
              <h2
                id="titre-raccourcis"
                className="mt-1 flex items-center gap-2 font-display text-lg font-semibold tracking-r-tight text-ink"
              >
                <FolderOpen className="h-5 w-5 text-rh" aria-hidden="true" />
                Mes raccourcis
              </h2>

              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {RACCOURCIS.map(({ href, label, description, icon: Icon }) => (
                  <li key={href}>
                    <button
                      type="button"
                      onClick={() => navigate(href)}
                      className="flex h-full w-full items-start gap-3 rounded-r-lg border border-border bg-surface/55 p-3 text-left backdrop-blur-glass transition-all hover:border-rh/40 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rh focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                    >
                      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block font-medium text-ink">{label}</span>
                        <span className="mt-0.5 block text-xs text-ink-muted">{description}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <p className="mt-3 flex items-start gap-2 text-xs text-ink-subtle">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                Ces écrans appartiennent à la brique Ressources humaines : vous y
                retrouvez exactement les mêmes données, rien n'est dupliqué ici.
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
